import { useState, useEffect, useRef } from 'react';

type ServerStatus = {
  online: boolean;
  loading: boolean;
};

/**
 * Hook for connecting to the server status SSE endpoint
 * Includes auto-reconnect functionality as specified in PRD
 */
export const useServerStatus = () => {
  const [status, setStatus] = useState<ServerStatus>({
    online: false,
    loading: true,
  });
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);

  // Create and configure the SSE connection
  const connectSSE = () => {
    // Clear any existing timeout
    if (reconnectTimeoutRef.current !== null) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      const sseUrl = `${apiUrl}/server/status/stream`;
      
      // Create a new EventSource
      const newEventSource = new EventSource(sseUrl, { withCredentials: true });
      eventSourceRef.current = newEventSource;
      
      // Set up event handlers
      newEventSource.onopen = () => {
        console.debug('SSE connection established');
      };

      newEventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setStatus({
            online: data.online,
            loading: false,
          });
          console.debug(`Server status update: ${data.online ? 'Online' : 'Offline'}`);
        } catch (error) {
          console.error('Error parsing SSE message:', error);
        }
      };

      newEventSource.onerror = (error) => {
        console.error('SSE connection error:', error);
        
        // Close the connection
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        
        // Mark as loading during reconnection attempt
        setStatus(prev => ({ ...prev, loading: true }));
        
        // Schedule reconnection after 3 seconds
        reconnectTimeoutRef.current = window.setTimeout(() => {
          reconnectTimeoutRef.current = null;
          connectSSE();
        }, 3000);
      };
    } catch (error) {
      console.error('Error setting up SSE connection:', error);
      // Schedule reconnection after 3 seconds
      reconnectTimeoutRef.current = window.setTimeout(() => {
        reconnectTimeoutRef.current = null;
        connectSSE();
      }, 3000);
    }
  };

  // Initialize SSE connection when component mounts
  useEffect(() => {
    connectSSE();
    
    // Clean up on unmount
    return () => {
      if (reconnectTimeoutRef.current !== null) {
        window.clearTimeout(reconnectTimeoutRef.current);
      }
      
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  return status;
};

export default useServerStatus; 